import { Highlight } from '@curology/ui-components-web/react/Highlight';
import { Typography } from '@curology/ui-components-web/react/Typography';
import React from 'react';

import { type Card } from '@/types/productSelectionAndCheckout/types';

import { IngredientsAccordion } from '../IngredientsAccordion';
import { ModalImage } from '../ModalImage';
import { ModalPricingVolumeAndProductOptionSelector } from '../ModalPricingVolumeAndProductOptionSelector';
import { ModalRatingAndNumReviews } from '../ModalRatingAndNumReviews';

export const CleanserAndMoisturizerModalContent = ({
  modalCard,
}: {
  modalCard: Card;
}) => {
  const { modal } = modalCard;

  return (
    <>
      <ModalImage modal={modal} />
      <Typography className="cur-mt-4" size="h2">
        {modal.title}
      </Typography>
      <ModalPricingVolumeAndProductOptionSelector modalCard={modalCard} />
      {modalCard.recommendationText && (
        <Highlight className="cur-mt-4" state="success">
          <div
            dangerouslySetInnerHTML={{
              __html: modalCard.recommendationText.modalCallout,
            }}
          ></div>
        </Highlight>
      )}
      <ModalRatingAndNumReviews modalCard={modalCard} />
      <Typography className="cur-mt-4" size="h3">
        Why it&apos;s special
      </Typography>
      <Typography className="cur-mt-4">
        Skincare is simple with the essential 3-step Curology routine: gentle
        cleanser, your personalized formula, and gel moisturizer for
        normal-to-oily skin. These dermatologist-designed essentials deliver
        clean, hydrated skin—without the worry of clogged pores.
      </Typography>
      <ul className="cur-list-disc cur-ml-4">
        <li className="cur-mt-4">
          <Typography>
            <strong>The cleanser:</strong> This is as gentle as it gets. Our
            lightly foaming face cleanser washes away dirt and excess
            oil—without stripping or clogging.
          </Typography>
        </li>
        <li className="cur-mt-4">
          <Typography>
            <strong>The moisturizer:</strong> Our refreshing, quick-absorbing
            gel moisturizer provides lightweight daily hydration for
            normal-to-oily skin. Layer it on to suit your skin&apos;s needs.
          </Typography>
        </li>
      </ul>
      <Typography className="cur-mt-4" size="h3">
        How to use it
      </Typography>
      <Typography className="cur-mt-4">
        Use morning and night as go-to essentials in your daily skincare
        routine.
      </Typography>
      <ul className="cur-list-disc cur-ml-4">
        <li className="cur-mt-4">
          <Typography>
            <strong>The cleanser:</strong> Massage the cleanser onto your damp
            skin. Rinse off thoroughly with warm water, then pat your skin dry.
          </Typography>
        </li>
        <li className="cur-mt-4">
          <Typography>
            <strong>The moisturizer:</strong> Apply the moisturizer after
            washing your face and patting your skin dry. Use as much you want—a
            thin layer if that feels good or multiple layers if you want extra
            hydration.
          </Typography>
        </li>
      </ul>
      <Typography className="cur-mt-4" size="h3">
        Featured ingredients
      </Typography>
      <Typography className="cur-mt-4" weight="bold">
        The cleanser
      </Typography>
      <IngredientsAccordion
        body="Locks water in the skin's outer layer to maximize moisture levels"
        title="Glycerin"
      />
      <Typography className="cur-mt-4" weight="bold">
        The moisturizer
      </Typography>
      <IngredientsAccordion
        body="Locks water in the skin's outer layer to maximize moisture levels"
        title="Glycerin"
      />
      <IngredientsAccordion
        body="Attracts and retains moisture for visibly plumper skin"
        title="Hyaluronic acid"
      />
      <Typography className="cur-mt-4" size="h3">
        Full ingredients list
      </Typography>
      <Typography className="cur-mt-4">
        <strong>The cleanser:</strong> Water/Aqua/Eau, Acrylates Copolymer,
        Cocamidopropyl Hydroxysultaine, Sodium Cocoyl Isethionate,
        Xylitylglucoside, Propanediol, Coco-glucoside, Anhydroxylitol, Stearic
        Acid, 1,2-Hexanediol, Xylitol, Panthenol, Coconut Alcohol, Citric Acid,
        PEG-150, Pentaerythrityl Tetrastearate, Stearyl Alcohol, Glycerin,
        Sodium Hydroxide, Sodium Chloride, Caprylhydroxamic Acid, Disodium EDTA,
        Xanthan Gum, Potassium Sorbate, Avena Sativa (Oat) Leaf Extract
      </Typography>
      <Typography className="cur-mt-4">
        <strong>The moisturizer:</strong> Water/Aqua/Eau, Dimethicone, Glycerin,
        Propanediol, Polysilicone-11, Cetearyl Olivate, Butylene Glycol,
        1,2-Hexanediol, Sodium Acrylate/Sodium Acryloyldimethyl Taurate
        Copolymer, Sorbitan Olivate, Polyisobutene, Carbomer, Aminomethyl
        Propanol, Citric Acid, Caprylhydroxamic Acid, Disodium EDTA, Sodium
        Hyaluronate, Dimethiconol, Caprylyl/Capryl Glucoside, Decyl Glucoside
      </Typography>
    </>
  );
};
